import {
	Flex,
	Text,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Grid,
	Box,
	Input,
	Spinner,
} from "@chakra-ui/react";
import CheckIcon from "../icons/CheckIcon";
import { type Command, CommandStatus } from "../../types/command.types";
import { useCallback, useMemo, useRef, useState } from "react";
import ClientSideTable from "../table/ClientSideTable";
import {
	type ColumnDef,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import OrderStatusBadge from "../OrderStatusBadge";
import TrashIcon from "../icons/TrashIcon";
import { useMutation, useQueryClient } from "react-query";
import { bulkChangeCommandStatus } from "../../mutations/command.mutations";
import { useSimpleToast } from "../../hooks/toast.hooks";
import FileInput from "../FileInput";
import { fetchCommandByExposedId } from "../../queries/command.queries";
import SelectAdmin from "../inputs/SelectAdmin";
import type { User } from "../../types/user.types";
import { getCommandMaterialContext } from "../../utils/getCommandMaterialContext";

const GRID_INPUT_NAME = "grille_name";
const GRID_INPUT_SCANNER_MIN_CODE_LENGTH = 4;
const GRID_INPUT_SCANNER_MAX_AVG_INTERVAL_MS = 35;

const isEditableElement = (target: EventTarget | null) => {
	if (!(target instanceof HTMLElement)) {
		return false;
	}
	if (target.isContentEditable) {
		return true;
	}
	return target.tagName === "INPUT" || target.tagName === "TEXTAREA";
};

const normalizeScannerCode = (code: string) => {
	return code.toLowerCase().split("§").join("-").trim();
};

type ChangeStatusProps = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	goBack: () => void;
	status: CommandStatus;
	setStatus: (status: CommandStatus) => void;
};

function ChangeStatusModal({
	isOpen,
	status,
	setStatus,
	onClose,
	goBack,
}: ChangeStatusProps) {
	const [step, setStep] = useState<number>(1);
	const [grillNumber, setGrillNumber] = useState<string>("");
	const [_, setPrintingFiles] = useState<File[] | null>(null);
	const [scannedCommands, setScannedCommands] = useState<Command[]>([]);
	const gridInputScannerBufferRef = useRef<{
		buffer: string;
		startedAt: number;
		lastKeyAt: number;
		previousGridValue: string;
	}>({
		buffer: "",
		startedAt: 0,
		lastKeyAt: 0,
		previousGridValue: "",
	});
	const toast = useSimpleToast();
	const resetGridInputScannerBuffer = useCallback(() => {
		gridInputScannerBufferRef.current = {
			buffer: "",
			startedAt: 0,
			lastKeyAt: 0,
			previousGridValue: "",
		};
	}, []);

	const rejectResinInInjecting = useCallback(
		(command: Command) => {
			if (
				status === CommandStatus.INJECTING &&
				getCommandMaterialContext(command).canInjectSilicone === false
			) {
				toast.info(
					"Scan",
					"Seules les commandes avec au moins une oreille en silicone peuvent être en injection.",
				);
				return true;
			}
			return false;
		},
		[status, toast],
	);

	const handleStatusChange = useCallback(
		(event: React.ChangeEvent<HTMLSelectElement>) => {
			setStep(1);
			setGrillNumber("");
			setPrintingFiles(null);
			resetGridInputScannerBuffer();
			const nextStatus =
				CommandStatus[event.target.value as keyof typeof CommandStatus];
			if (nextStatus === CommandStatus.INJECTING) {
				const filteredCommands = scannedCommands.filter(
					(command) => getCommandMaterialContext(command).canInjectSilicone,
				);
				if (filteredCommands.length < scannedCommands.length) {
					toast.info(
						"Scan",
						"Seules les commandes avec au moins une oreille en silicone peuvent être en injection.",
					);
					setScannedCommands(filteredCommands);
				}
			}
			setStatus(nextStatus);
		},
		[resetGridInputScannerBuffer, scannedCommands, setStatus, setStep, toast],
	);
	const queryClient = useQueryClient();

	const { mutate: bulkChangeStatus, isLoading: bulkChangeStatusIsLoading } =
		useMutation(bulkChangeCommandStatus, {
			onSuccess: async () => {
				await queryClient.invalidateQueries();
				toast.success(
					"Commandes",
					"Les statuts des commandes ont été mis à jour",
				);
				onClose();
			},
		});
	const [manager, setManager] = useState<User | undefined>(undefined);
	const requiresModeler =
		status === CommandStatus.WAITING_FOR_MODELING ||
		status === CommandStatus.MODELING;
	const handleConfirm = useCallback(() => {
		if (requiresModeler && !manager) {
			toast.error(
				"Modeleur requis",
				"Veuillez ajouter un modeleur pour la modélisation.",
			);
			return;
		}
		if (step === 1 && status === CommandStatus.PRINTING) {
			setStep(2);
		} else {
			bulkChangeStatus({
				ids: scannedCommands.map((sc) => sc.id),
				status: status,
				modelerId: manager?.id,
			});
		}
	}, [
		bulkChangeStatus,
		manager,
		requiresModeler,
		status,
		step,
		scannedCommands,
		toast,
	]);

	const handleRemoveItem = useCallback(
		(command: Command) => {
			setScannedCommands(scannedCommands.filter((sc) => sc.id !== command.id));
		},
		[scannedCommands],
	);

	const handleGoBack = useCallback(() => {
		setStep(1);
		resetGridInputScannerBuffer();
		goBack();
	}, [resetGridInputScannerBuffer, setStep, goBack]);
	const shouldHandleScannerInput = useCallback(
		(target: EventTarget | null) => {
			return !isEditableElement(target);
		},
		[],
	);

	const commandCode = useRef("");
	const tryAppendScannedCommand = useCallback(
		async (rawCode: string) => {
			const code = normalizeScannerCode(rawCode);
			if (code.length === 0) {
				return;
			}
			if (
				scannedCommands.findIndex(
					(command) => command.exposedId.toString() === code,
				) !== -1
			) {
				toast.info("Scan", "Le code est déjà présent dans la liste.");
				return;
			}
			const command = await fetchCommandByExposedId(code);
			if (command !== null && !rejectResinInInjecting(command)) {
				setScannedCommands((currentCommands) => {
					if (
						currentCommands.findIndex(
							(item) =>
								item.exposedId.toString() === command.exposedId.toString(),
						) !== -1
					) {
						return currentCommands;
					}
					return [command, ...currentCommands];
				});
			}
		},
		[rejectResinInInjecting, scannedCommands, toast],
	);
	const handleScannerInputPaste = useCallback(
		async (event: React.ClipboardEvent) => {
			try {
				const text = event.clipboardData.getData("text");
				await tryAppendScannedCommand(text);
			} catch (err) {
				toast.error("Scan", "Le code n'a pas pu être lu.");
			}
		},
		[toast, tryAppendScannedCommand],
	);
	const handleScannerOnKeyDown = async (event: React.KeyboardEvent) => {
		switch (event.code) {
			case "Enter": {
				const code = normalizeScannerCode(commandCode.current);
				commandCode.current = "";
				if (code.length > 0) {
					await tryAppendScannedCommand(code);
				}
				commandCode.current = "";
				break;
			}
			case "Shift":
			case "Meta":
			case "ShiftLeft":
				break;
			default: {
				commandCode.current = commandCode.current + event.key;
				break;
			}
		}
	};
	const handleGridInputScannerOnKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (status !== CommandStatus.PRINTING || step !== 1) {
				resetGridInputScannerBuffer();
				return;
			}
			const target = event.target;
			if (
				!(target instanceof HTMLInputElement) &&
				!(target instanceof HTMLTextAreaElement)
			) {
				resetGridInputScannerBuffer();
				return;
			}
			const isGridInput =
				target instanceof HTMLInputElement && target.name === GRID_INPUT_NAME;
			switch (event.code) {
				case "Shift":
				case "Meta":
				case "Control":
				case "Alt":
				case "ShiftLeft":
				case "ShiftRight":
				case "MetaLeft":
				case "MetaRight":
				case "ControlLeft":
				case "ControlRight":
				case "AltLeft":
				case "AltRight":
					return;
				case "Enter": {
					const scannerState = gridInputScannerBufferRef.current;
					const code = normalizeScannerCode(scannerState.buffer);
					const elapsed = scannerState.lastKeyAt - scannerState.startedAt;
					const avgInterval =
						code.length > 1 ? elapsed / (code.length - 1) : Number.POSITIVE_INFINITY;
					const isLikelyScannerInput =
						code.length >= GRID_INPUT_SCANNER_MIN_CODE_LENGTH &&
						avgInterval <= GRID_INPUT_SCANNER_MAX_AVG_INTERVAL_MS;
					const previousGridValue = scannerState.previousGridValue;
					resetGridInputScannerBuffer();
					if (!isLikelyScannerInput) {
						return;
					}
					event.preventDefault();
					if (isGridInput) {
						setGrillNumber(previousGridValue);
					}
					void tryAppendScannedCommand(code);
					return;
				}
				default:
					break;
			}
			if (event.key.length !== 1) {
				return;
			}
			const now = Date.now();
			const scannerState = gridInputScannerBufferRef.current;
			const isContinuation =
				now - scannerState.lastKeyAt <= GRID_INPUT_SCANNER_MAX_AVG_INTERVAL_MS;
			if (!isContinuation) {
				gridInputScannerBufferRef.current = {
					buffer: event.key,
					startedAt: now,
					lastKeyAt: now,
					previousGridValue: isGridInput ? grillNumber : "",
				};
				return;
			}
			gridInputScannerBufferRef.current = {
				...scannerState,
				buffer: `${scannerState.buffer}${event.key}`,
				lastKeyAt: now,
			};
		},
		[
			grillNumber,
			resetGridInputScannerBuffer,
			setGrillNumber,
			status,
			step,
			tryAppendScannedCommand,
		],
	);

	const columns = useMemo<ColumnDef<Command>[]>(
		() => [
			{
				accessorFn: (row) => row.exposedId,
				id: "exposed_id",
				header: "ID",
				cell: (info) => info.getValue(),
			},
			{
				accessorFn: (row) => {
					if (row.patient) {
						return `${row.patient.firstName} ${row.patient.lastName}`;
					}
					return `---`;
				},
				id: "patient",
				header: "Patient",
				cell: (info) => info.getValue(),
			},
			{
				accessorFn: (row) => format(row.createdAt, "dd/MM/yyyy"),
				id: "created_at",
				header: "Date de création",
				cell: (info) => info.getValue(),
			},
			{
				accessorFn: (row) => (
					<OrderStatusBadge
						halted={row.haltedAt != null}
						status={row.status}
					></OrderStatusBadge>
				),
				id: "status",
				header: "Statut",
				cell: (info) => info.getValue(),
			},
			{
				accessorFn: (row) => (
					<Flex justifyContent={"end"}>
						<TrashIcon
							color={"gray.500"}
							onClick={() => handleRemoveItem(row)}
						></TrashIcon>
					</Flex>
				),
				id: "more",
				header: "",
				cell: (info) => info.getValue(),
			},
		],
		[handleRemoveItem],
	);
	const table = useReactTable({
		data: scannedCommands ?? [],
		columns: columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});
	return (
		<Modal isOpen={isOpen} onClose={() => {}} isCentered>
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6.9px)" />
			<ModalContent
				maxW={"none"}
				w="min(1800px, 80vw)"
				onPaste={(event) => {
					if (!shouldHandleScannerInput(event.target)) {
						return;
					}
					event.preventDefault();
					void handleScannerInputPaste(event);
				}}
				onKeyDown={(event) => {
					if ((event.ctrlKey || event.metaKey) && event.key === "v") {
						return;
					}
					if (!shouldHandleScannerInput(event.target)) {
						handleGridInputScannerOnKeyDown(event);
						return;
					}
					resetGridInputScannerBuffer();
					event.preventDefault();
					void handleScannerOnKeyDown(event);
				}}
			>
				<ModalHeader>
					<Flex alignItems={"center"}>
						<Flex
							w="48px"
							h="48px"
							bg="zinc.50"
							border="solid 1px"
							borderColor="brand.100"
							justifyContent="center"
							alignItems="center"
							borderRadius={100}
							mr="8px"
						>
							<CheckIcon w="24px" h="24px" color="brand.700"></CheckIcon>
						</Flex>
						<Text lineHeight={"48px"}>Modifier le statut à</Text>
						<Select
							value={status}
							onChange={handleStatusChange}
							border={0}
							w="fit-content"
							backgroundColor={"brand.600"}
							color={"white"}
							h="1.5em"
							pl={4}
						>
							<option value={CommandStatus.WAITING_FOR_RECEPTION}>
								Attente de réception
							</option>
							<option value={CommandStatus.RECEIVED}>Receptionné</option>
							<option value={CommandStatus.WAITING_FOR_MODELING}>
								En attente de modélisation
							</option>
							<option value={CommandStatus.MODELING}>En modélisation</option>
							<option value={CommandStatus.WAITING_FOR_PRINTING}>
								Prête pour impression
							</option>
							<option value={CommandStatus.PRINTING}>En impression</option>
							<option value={CommandStatus.PRINTED}>Imprimé</option>
							<option value={CommandStatus.WAITING_FOR_LAB}>
								À traiter labo
							</option>
							<option value={CommandStatus.INJECTING}>En injection</option>
							<option value={CommandStatus.POLISHING}>En finition</option>
							<option value={CommandStatus.QUALITY_CONTROL}>
								Contrôle qualité
							</option>
							<option value={CommandStatus.READY}>Prête</option>
							<option value={CommandStatus.READY_FOR_DELIVERY}>
								Prêt à expédier
							</option>
							<option value={CommandStatus.IN_DELIVERY}>
								Expédié
							</option>
							<option value={CommandStatus.DELIVERED}>
								Livré
							</option>
							<option value={CommandStatus.ABORTED}>Annulé</option>
							<option value={CommandStatus.PENDING}>En pause</option>
						</Select>
					</Flex>
				</ModalHeader>
				<ModalCloseButton onClick={onClose} />
				<ModalBody>
					{status === CommandStatus.PRINTING && step === 1 && (
						<Flex
							justifyContent="center"
							alignItems="center"
							direction="column"
						>
							<Input
								w="400px"
								placeholder="Numéro de la grille d'impression"
								name={"grille_name"}
								value={grillNumber}
								onChange={(event) => setGrillNumber(event.target.value)}
							></Input>
							<Box py={4} w="100%">
								<FileInput
									name={""}
									maxNumberOfFiles={1}
									onFileChange={(files: File[] | null) =>
										setPrintingFiles(files)
									}
								></FileInput>
							</Box>
						</Flex>
					)}
					{(status !== CommandStatus.PRINTING || step === 2) && (
						<>
							<SelectAdmin
								placeholder="Ajouter un modeleur"
								user={manager}
								setUser={(manager) => {
									setManager(manager);
								}}
							></SelectAdmin>
							<ClientSideTable
								table={table}
								w={["auto", "240px", "200px", "300px", "10px"]}
								flexGrow={[1, 0, 0, 0, 0]}
							></ClientSideTable>
						</>
					)}
				</ModalBody>
				<ModalFooter borderTop="solid 1px #E2E8F0">
					<Grid gridTemplateColumns={"1fr 1fr"} gap="40px" w="100%">
						<Flex p={3} justifyContent="center" onClick={handleGoBack}>
							Retour
						</Flex>
						{bulkChangeStatusIsLoading ? (
							<Flex
								p={3}
								justifyContent="center"
								bg="brand.300"
								color="white"
								borderRadius={"xl"}
								onClick={handleConfirm}
							>
								<Spinner pr={4} />
								Confirmer
							</Flex>
						) : (
							<Flex
								p={3}
								justifyContent="center"
								bg="brand.700"
								color="white"
								borderRadius={"xl"}
								onClick={handleConfirm}
							>
								Confirmer
							</Flex>
						)}
					</Grid>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

export default ChangeStatusModal;
